import api from '@/api';
import useMeQuery from '@/api/queries/me/useMeQuery';
import useUpdateCachedUser from '@/api/queries/useUpdateCachedUser';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { AxiosError } from 'axios';
import type { Ref } from 'vue';
import type { User } from '@/api/queries/user/types/user';
import { useLocaleStore } from '@/stores/locales';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toastification';
import { inject } from 'vue';
import type { Translate } from '@/plugins/i18n/types';

const patchUser = async (uuid?: string, user?: Partial<User>) => {
  if (!uuid) throw new AxiosError('Auth UUID is necessary to patch user');

  const res = await api<User>({
    method: 'patch',
    url: `/user/${uuid}`,
    data: {
      ...user,
    },
  });
  return res;
};

const deleteUser = async (uuid?: string, reason?: string) => {
  if (!uuid) throw new AxiosError('Auth UUID is necessary to delete user');
  if (!reason) throw new AxiosError('Reason is necessary to delete user');

  const res = await api<void>({
    method: 'delete',
    url: `/user/${uuid}`,
    data: {
      delete_reason: reason,
    },
  });

  return res;
};

export function useDeleteAccountMutation() {
  return useMutation({
    mutationFn: ({ reason, uuid }: { reason: string; uuid: string }) => deleteUser(uuid, reason),
  });
}

export function useUserMutation(
  user: Ref<Partial<User> | undefined>,
  options?: { silent?: boolean },
) {
  const { auth } = useMeQuery();
  const toast = useToast();
  const t = inject('t') as Translate;

  const { currentLocale } = storeToRefs(useLocaleStore());
  const { silent = false } = options ?? {};

  const queryClient = useQueryClient();

  const { mutateAsync: updateUser, isPending: isLoading } = useMutation({
    mutationFn: () => patchUser(auth.value?.uuid, user.value),

    onSuccess: (data) => {
      useUpdateCachedUser(data as User, queryClient);

      if (data?.locale_id !== currentLocale.value.id) {
        useLocaleStore().updateLocale(data?.locale_id);
      }
      if (!silent) {
        toast.success(t('general.save_success'));
      }
    },
    onError: () => {
      if (!silent) toast.error(t('general.error.general'));
    },
  });

  return { updateUser, isLoading };
}
